/**
 * The money over the whole project register, above the list that shows it.
 *
 * A file of its own rather than more lines in `ProjectsList`: this has a fetch,
 * a loading state and a rule about when it is allowed to speak, which is a job
 * and not a decoration on the list's job. The list is the page - it must draw
 * even when this endpoint is down, so the two never share a request, a loading
 * flag or a failure (AGENTS.md §4).
 *
 * The figures come from GET /admin/crm/projects/summary and nothing here is
 * computed from the rows on screen. That is the whole point of the endpoint: the
 * list is one page of 25, so any total added up from it would describe the page
 * and be read as describing the register.
 *
 * THE SET THIS DESCRIBES. It is not chosen here. The caller hands over the same
 * narrowing filters it sent to the register - status, client, deadline window,
 * tag, member, archive - assembled once in `listView.ts`, so the header and the
 * rows underneath it cannot end up answering different questions. This file no
 * longer names the filters at all, which is the point: the day an eighth one is
 * added, it arrives here without an edit.
 *
 * WHEN THIS STRIP MUST NOT SPEAK. The one filter the endpoint has no answer for
 * is the search box, and `portfolioParams` says so by handing back `null`. A
 * strip that keeps showing the register's total while the list below it shows
 * the three projects somebody searched for is not a summary of what the operator
 * is looking at; it is a different question answered in the same box, and at a
 * glance the big number wins. So it draws nothing at all - the same call
 * `ProjectsToolbar` makes about the status field on the roadmap: a control that
 * cannot answer the current question is absent rather than present and wrong.
 * Labelling it "whole register" was the alternative and it loses - the label is
 * 11px, the figure is 18px and bold, and the misreading it invites is the exact
 * bug this rollup exists to prevent.
 */
import { useEffect, useRef, useState, type CSSProperties } from "react";
import { cn } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import { getProjectPortfolio, type ProjectFilterParams, type ProjectPortfolio } from "../../../lib/projects";
import { Card, fmtNum } from "../../ui";
import { fmtHours, money } from "./lib";

/**
 * One figure. The same markup as the project page's KPI strip and the sales
 * one, so a total means the same thing and looks the same wherever an operator
 * meets it.
 *
 * `mut` is not decoration. A count of zero is an answer - nothing is over
 * budget - and it has to stay on screen rather than be hidden, or the operator
 * cannot tell "none" from "this tile did not load". Muted is how the invoice
 * list already draws a zero payment: present, readable, not shouting.
 */
function Stat({ label, value, tone }: { label: string; value: string; tone?: "lt" | "mut" }) {
  return (
    <div className="crm-q">
      <span>
        <span className="crm-q-lab block">{label}</span>
        <span
          className={cn(
            "crm-q-val block",
            tone === "lt" && "text-red-600",
            tone === "mut" && "text-muted-foreground",
          )}
        >
          {value}
        </span>
      </span>
    </div>
  );
}

export function PortfolioStrip({
  /**
   * Exactly the filters the list is under, or `null` when the rollup cannot
   * honestly answer the question being asked - see the note at the top. Already
   * resolved by the caller, including the roadmap's exemption for the status
   * filter: the board draws every status as a column of its own and ignores it,
   * so the rollup above the board has to ignore it too.
   */
  params,
}: {
  params: ProjectFilterParams | null;
}) {
  const t = useT();
  const [data, setData] = useState<ProjectPortfolio | null>(null);

  /**
   * The question as a string, because the effect has to refire when the filters
   * CHANGE and not when the caller re-renders. `params` is a fresh object every
   * time the register redraws, so depending on it directly would put this strip
   * in a fetch loop - one request per render, forever, against an endpoint that
   * walks the whole project set. The object itself is read through a ref, which
   * always holds the same filters the key was computed from.
   */
  const key = params ? JSON.stringify(params) : "";
  const latest = useRef(params);
  latest.current = params;

  useEffect(() => {
    if (!key) {
      setData(null);
      return;
    }
    let live = true;
    // Cleared before the request, not after it. The numbers on screen belong to
    // the filter that is being replaced, and holding them through the wait would
    // print one set's totals over another set's list for as long as the fetch
    // takes. Nothing is drawn until the answer for THIS filter arrives, which is
    // what every other strip in the CRM does while it loads.
    setData(null);
    getProjectPortfolio(latest.current ?? undefined)
      .then((res) => {
        if (live) setData(res);
      })
      // Swallowed on purpose. The list owns the page and has its own request;
      // a rollup that failed to load is a strip that is not there, not an error
      // banner over a register that loaded perfectly well.
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [key]);

  if (!data) return null;

  return (
    <Card
      className="crm-quick"
      style={{ "--crm-quick-cols": 5 } as CSSProperties}
      role="group"
      aria-label={t("projects.portfolio")}
    >
      {/* `budgetCents` sums only the projects that HAVE a budget, so this is not
          comparable to `spentCents` project for project - which is why the
          over-budget count is its own tile rather than a colour on the spend. */}
      <Stat label={t("projects.portfolioBudget")} value={money(data.budgetCents)} />
      <Stat label={t("projects.portfolioSpent")} value={money(data.spentCents)} />
      <Stat label={t("projects.portfolioInvoiced")} value={money(data.invoicedCents)} />
      <Stat label={t("projects.portfolioUnbilled")} value={`${fmtHours(data.unbilledMinutes)} h`} />
      {/* Decided in cents on the server, per project, and never re-derived here
          from a rounded percentage - that rounding is what once made a project
          at 100.2% read as healthy on one screen and over budget on another. */}
      <Stat
        label={t("projects.portfolioOverBudget")}
        value={fmtNum(data.overBudgetCount)}
        tone={data.overBudgetCount > 0 ? "lt" : "mut"}
      />
    </Card>
  );
}
